import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
} from '@/components/ui';
import {
  Plus,
  Copy,
  QrCode,
  RefreshCw,
  ExternalLink,
  Landmark,
  AlertCircle,
  Loader2,
  CheckCircle,
  ArrowDownToLine,
} from 'lucide-react';
import { api } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { formatDate } from '@/lib/utils';
import { CreateAddressModal } from '@/components/CreateAddressModal';
import { AddressQRModal } from '@/components/AddressQRModal';

interface BankAccount {
  accountNumber: string;
  accountName?: string;
  networkId: string;
  bankName?: string;
}

interface AddressStats {
  totalDeposits: number;
  totalVolume: number;
  lastDepositAt?: string;
  currentBalance?: number;
}

interface StaticAddress {
  id: string;
  depositAddress: string;
  originNetwork: string;
  originAsset?: string;
  smartAccountAddress: string;
  destinationHash: string;
  status: 'active' | 'inactive' | 'suspended';
  bankAccount: BankAccount;
  currency: string;
  countryCode: string;
  createdAt: string;
  lastActivityAt?: string;
  stats?: AddressStats;
}

const networkInfo: Record<string, { name: string; icon?: string; color: string }> = {
  BASE: { name: 'Base', color: 'bg-blue-500' },
  POLYGON: { name: 'Polygon', color: 'bg-purple-500' },
  ARB: { name: 'Arbitrum', color: 'bg-blue-600' },
  OP: { name: 'Optimism', color: 'bg-red-500' },
  SOL: { name: 'Solana', color: 'bg-gradient-to-r from-purple-500 to-blue-500' },
  AVAX: { name: 'Avalanche', color: 'bg-red-600' },
  BSC: { name: 'BNB Chain', color: 'bg-yellow-500' },
};

export default function StaticAddressesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [addresses, setAddresses] = useState<StaticAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<{ [key: string]: boolean }>({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<StaticAddress | null>(null);
  const [kycVerified, setKycVerified] = useState(false);
  const [hasBankAccounts, setHasBankAccounts] = useState(false);

  useEffect(() => {
    loadAddresses();
    checkPrerequisites();
  }, []);

  const loadAddresses = async () => {
    try {
      setLoading(true);
      const response = await api.staticAddresses.list();
      setAddresses(response.data.addresses || []);
    } catch (error: any) {
      console.error('Failed to load static addresses:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to load addresses',
        description: error.response?.data?.message || 'Please try again later',
      });
    } finally {
      setLoading(false);
    }
  };

  const checkPrerequisites = async () => {
    try {
      // Check KYC status
      const kycResponse = await api.kyc.status();
      setKycVerified(kycResponse.data.isVerified === true);

      // Check bank accounts
      const bankResponse = await api.bankAccounts.list();
      setHasBankAccounts(bankResponse.data.accounts?.length > 0);
    } catch (error) {
      console.error('Failed to check prerequisites:', error);
    }
  };

  const handleCreateAddress = async (data: any) => {
    try {
      const response = await api.staticAddresses.create(data);

      toast({
        title: 'Address created successfully',
        description: 'Your static deposit address is ready to use',
      });

      // Reload addresses list
      await loadAddresses();
      setCreateModalOpen(false);

      // Show QR code for the new address
      const newAddress: StaticAddress = {
        id: response.data.destinationHash,
        depositAddress: response.data.depositAddress,
        originNetwork: response.data.originNetwork,
        smartAccountAddress: response.data.smartAccountAddress,
        destinationHash: response.data.destinationHash,
        status: 'active',
        bankAccount: response.data.settlement.bankAccount,
        currency: response.data.settlement.currency,
        countryCode: 'NG', // TODO: Get from response
        createdAt: new Date().toISOString(),
        lastActivityAt: undefined,
      };

      setSelectedAddress(newAddress);
      setQrModalOpen(true);
    } catch (error: any) {
      console.error('Failed to create address:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to create address',
        description: error.response?.data?.message || 'Please try again later',
      });
    }
  };

  const handleSyncBalance = async (address: StaticAddress) => {
    try {
      setSyncing({ ...syncing, [address.id]: true });

      const response = await api.staticAddresses.sync(address.id);

      toast({
        title: 'Balance synced',
        description: response.data.balance
          ? `Current balance: ${response.data.balance} ${address.currency}`
          : 'No pending balance',
      });

      // Reload addresses to get updated stats
      await loadAddresses();
    } catch (error: any) {
      console.error('Failed to sync balance:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to sync balance',
        description: error.response?.data?.message || 'Please try again later',
      });
    } finally {
      setSyncing({ ...syncing, [address.id]: false });
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: `${label} copied to clipboard`,
    });
  };

  const truncateAddress = (address: string) => {
    if (!address) return '';
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency === 'NGN' ? 'NGN' : 'USD',
      minimumFractionDigits: currency === 'NGN' ? 0 : 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Static Deposit Addresses</h1>
        <p className="text-gray-600">
          Permanent addresses for receiving crypto that auto-converts to your local currency
        </p>
      </div>

      {/* Prerequisites Check */}
      {(!kycVerified || !hasBankAccounts) && (
        <Card className="mb-6 border-yellow-500 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              Setup Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {!kycVerified && (
                <li className="flex items-center gap-2">
                  <span className="text-red-500">✗</span>
                  <span>Complete KYC verification</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/kyc')}
                    className="ml-auto"
                  >
                    Verify Now →
                  </Button>
                </li>
              )}
              {!hasBankAccounts && (
                <li className="flex items-center gap-2">
                  <span className="text-red-500">✗</span>
                  <span>Add a bank account</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/bank-accounts')}
                    className="ml-auto"
                  >
                    Add Account →
                  </Button>
                </li>
              )}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Action Bar */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <Badge variant="neutral" className="text-sm">
            {addresses.length} {addresses.length === 1 ? 'Address' : 'Addresses'}
          </Badge>
        </div>
        <Button
          onClick={() => setCreateModalOpen(true)}
          disabled={!kycVerified || !hasBankAccounts}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Address
        </Button>
      </div>

      {/* Addresses Grid */}
      {addresses.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Landmark className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No addresses yet</h3>
            <p className="text-gray-600 mb-4">
              Create your first static deposit address to start receiving crypto payments
            </p>
            <Button
              onClick={() => setCreateModalOpen(true)}
              disabled={!kycVerified || !hasBankAccounts}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Address
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {addresses.map((address) => (
            <Card key={address.id} className="relative">
              <CardHeader className="pb-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Badge
                        variant="neutral"
                        className={`${networkInfo[address.originNetwork]?.color} text-white`}
                      >
                        {networkInfo[address.originNetwork]?.name || address.originNetwork}
                      </Badge>
                      {address.originAsset && (
                        <span className="text-sm text-gray-600">
                          {address.originAsset}
                        </span>
                      )}
                    </CardTitle>
                    <CardDescription>
                      To: {address.bankAccount.accountNumber}
                    </CardDescription>
                  </div>
                  <Badge variant={address.status === 'active' ? 'success' : 'neutral'}>
                    {address.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Address Display */}
                <div>
                  <label className="text-sm text-gray-600">Deposit Address</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded flex-1 truncate">
                      {truncateAddress(address.depositAddress)}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(address.depositAddress, 'Address')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedAddress(address);
                        setQrModalOpen(true);
                      }}
                    >
                      <QrCode className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Stats */}
                {address.stats && (
                  <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                    <div>
                      <p className="text-sm text-gray-600">Total Deposits</p>
                      <p className="font-semibold">{address.stats.totalDeposits || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Volume</p>
                      <p className="font-semibold">
                        {formatCurrency(address.stats.totalVolume || 0, address.currency)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                    onClick={() => navigate(`/static-addresses/${address.id}`)}
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Details
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleSyncBalance(address)}
                    disabled={syncing[address.id]}
                  >
                    {syncing[address.id] ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                {/* Last Activity */}
                {address.lastActivityAt && (
                  <p className="text-xs text-gray-500 text-center">
                    Last deposit: {formatDate(address.lastActivityAt)}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* How It Works Section */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>How Static Addresses Work</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <Plus className="h-6 w-6 text-blue-600" />
              </div>
              <h4 className="font-semibold mb-1">1. Create Address</h4>
              <p className="text-sm text-gray-600">
                Link your bank account and get a permanent address
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <ArrowDownToLine className="h-6 w-6 text-green-600" />
              </div>
              <h4 className="font-semibold mb-1">2. Receive Crypto</h4>
              <p className="text-sm text-gray-600">
                Anyone can send crypto to your address anytime
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <RefreshCw className="h-6 w-6 text-purple-600" />
              </div>
              <h4 className="font-semibold mb-1">3. Auto-Convert</h4>
              <p className="text-sm text-gray-600">
                Crypto automatically converts to your local currency
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <CheckCircle className="h-6 w-6 text-orange-600" />
              </div>
              <h4 className="font-semibold mb-1">4. Bank Deposit</h4>
              <p className="text-sm text-gray-600">
                Funds deposited to your bank account instantly
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      {createModalOpen && (
        <CreateAddressModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSubmit={handleCreateAddress}
        />
      )}

      {qrModalOpen && selectedAddress && (
        <AddressQRModal
          open={qrModalOpen}
          onClose={() => setQrModalOpen(false)}
          address={selectedAddress}
        />
      )}
    </div>
  );
}